import { t } from '../../utils/i18n';

/**
 * Renders the month/day/year DOB dropdown triple into `host` and keeps a
 * hidden input in sync with the combined YYYY-MM-DD value (Laravel date
 * format). Shared by the inline consult-form DOB field and the standalone
 * DOB step screen so both stay identical.
 *
 * @param {HTMLElement} host container to render into
 * @param {object} opts
 * @param {string} opts.inputId id for the hidden input holding YYYY-MM-DD
 * @param {string} opts.idPrefix prefix for the three select ids (kept unique
 *   per screen so the two render sites can never collide on ids)
 * @param {Function} [opts.onChange] called after every value recompute
 */
export function renderDobDropdowns(host, { inputId, idPrefix, onChange } = {}) {
    // Build month options using moment locale for localized names
    const months = moment.months();
    const monthOpts = months
        .map((m, i) => `<option value="${i + 1}">${m}</option>`)
        .join('');

    // Build day options (1-31, refined on month/year change)
    let dayOpts = '';
    for (let d = 1; d <= 31; d++) dayOpts += `<option value="${d}">${d}</option>`;

    // Build year options (current year down to 1920)
    const currentYear = new Date().getFullYear();
    let yearOpts = '';
    for (let y = currentYear; y >= 1920; y--) yearOpts += `<option value="${y}">${y}</option>`;

    host.innerHTML = `
        <input id="${inputId}" type="hidden">
        <label class="dob-dropdowns-label">${t('dob')}</label>
        <div class="dob-dropdowns">
            <select id="${idPrefix}Month" aria-label="${t('month')}">
                <option value="" disabled selected>${t('month')}</option>
                ${monthOpts}
            </select>
            <select id="${idPrefix}Day" aria-label="${t('day')}">
                <option value="" disabled selected>${t('day')}</option>
                ${dayOpts}
            </select>
            <select id="${idPrefix}Year" aria-label="${t('year')}">
                <option value="" disabled selected>${t('year')}</option>
                ${yearOpts}
            </select>
        </div>
    `;

    const monthEl = host.querySelector(`#${idPrefix}Month`);
    const dayEl = host.querySelector(`#${idPrefix}Day`);
    const yearEl = host.querySelector(`#${idPrefix}Year`);
    const hiddenInput = host.querySelector(`#${inputId}`);

    function updateDobValue() {
        const m = monthEl.value;
        const d = dayEl.value;
        const y = yearEl.value;
        if (m && d && y) {
            // YYYY-MM-DD format for Laravel date validation
            hiddenInput.value = `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
        } else {
            hiddenInput.value = '';
        }
        if (typeof onChange === 'function') onChange(hiddenInput.value);
    }

    // Adjust day options when month or year changes
    function adjustDays() {
        const m = parseInt(monthEl.value, 10);
        const y = parseInt(yearEl.value, 10) || currentYear;
        const maxDay = m ? new Date(y, m, 0).getDate() : 31;
        const prevDay = dayEl.value;
        let opts = '<option value="" disabled>Day</option>';
        for (let d = 1; d <= maxDay; d++) opts += `<option value="${d}"${d === parseInt(prevDay, 10) ? ' selected' : ''}>${d}</option>`;
        dayEl.innerHTML = opts;
        // If previously selected day exceeds new max, clear it
        if (parseInt(prevDay, 10) > maxDay) dayEl.value = '';
    }

    monthEl.addEventListener('change', () => { adjustDays(); updateDobValue(); });
    dayEl.addEventListener('change', updateDobValue);
    yearEl.addEventListener('change', () => { adjustDays(); updateDobValue(); });
}
